import { useEffect, useState } from 'react';
import {
  collection,
  documentId,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  DailyOperationSchema,
  type DailyOperation,
  type Period,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/operation/useOperation';

export type OperationSummary = {
  printsByProtocol: Record<string, number>;
  printsBySize: Record<string, number>;
  failuresByProtocol: Record<string, number>;
  failuresByReason: Record<string, number>;
  connectionsByModel: Record<string, number>;
  totalPrints: number;
  totalFailures: number;
  totalConnections: number;
};

export type OperationState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; summary: OperationSummary; days: number };

function mergeInto(
  target: Record<string, number>,
  src: Record<string, number>,
): void {
  for (const [k, v] of Object.entries(src)) target[k] = (target[k] ?? 0) + v;
}

/** Soma os docs diários do período num único agregado. Pura → testável. */
export function summarize(docs: DailyOperation[]): OperationSummary {
  const s: OperationSummary = {
    printsByProtocol: {},
    printsBySize: {},
    failuresByProtocol: {},
    failuresByReason: {},
    connectionsByModel: {},
    totalPrints: 0,
    totalFailures: 0,
    totalConnections: 0,
  };
  for (const d of docs) {
    mergeInto(s.printsByProtocol, d.printsByProtocol);
    mergeInto(s.printsBySize, d.printsBySize);
    mergeInto(s.failuresByProtocol, d.failuresByProtocol);
    mergeInto(s.failuresByReason, d.failuresByReason);
    mergeInto(s.connectionsByModel, d.connectionsByModel ?? {});
    s.totalPrints += d.totalPrints ?? 0;
    s.totalFailures += d.totalFailures ?? 0;
    s.totalConnections += d.totalConnections ?? 0;
  }
  return s;
}

/**
 * Carrega os agregados de operação (impressão) de
 * `/aggregations/operation/days/` no range do período e soma num único
 * resumo. Espelha o padrão de `useProduct` (leitura por documentId range).
 *
 * Origem: #68 — commit 9ddffdfe8, 2026-06-20 (dashboard Operação consome
 * `aggregateOperation`; a rota nasceu com placeholders em f8a89a64d).
 *
 * Contrato: `period` (datas `YYYY-MM-DD`) → `OperationState`, leitura
 * one-shot; `days` = quantos docs válidos entraram na soma. Doc fora do
 * `DailyOperationSchema` é pulado só com `console.warn`; erro → `ERROR` só com
 * `console.error` — sem report ao PostHog (achado registrado na #926).
 *
 * Usado por: `OperationDashboardPage.tsx` (rota `/dashboard/operation`; web
 * `apps/admin`, super-admin).
 *
 * Arquitetura: só leitura de coleção direta via
 * `match /aggregations/{type}/{subcoll}/{id}` de `firebase/firestore.rules`
 * (`isSuperAdmin()`); intervalo por `documentId()` não pede índice composto.
 * Escritor: o scheduler `aggregateOperation` (Functions), que consulta os
 * eventos `print_executed`/`print_failed`/`printer_connected` do PostHog.
 *
 * Dados (anonimizado; `DailyOperationSchema` em
 * `packages/types/src/aggregations.ts`):
 *   aggregations/operation/days/{yyyymmdd}
 *     { "yyyymmdd": "20261007", "schemaVersion": 1,
 *       "printsByProtocol": { "TSPL": 120 }, "printsBySize": { "50x30": 120 },
 *       "failuresByProtocol": { "TSPL": 2 }, "failuresByReason": { "TIMEOUT": 2 },
 *       "connectionsByModel": { "<modelo BT>": 4 },
 *       "totalPrints": 120, "totalFailures": 2, "totalConnections": 4,
 *       "computedAt": 1759300000000 }
 *   — `total*: null` significa que a Query API do PostHog falhou naquele dia;
 *     `summarize` soma como 0, então o total do período não mostra o "—" que
 *     o schema prevê (achado registrado na #926).
 *
 * Issues: #68, #926.
 */
export function useOperation(period: Period): OperationState {
  const [state, setState] = useState<OperationState>({ kind: 'LOADING' });

  const periodFrom = period.from;
  const periodTo = period.to;

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });

    async function load(): Promise<void> {
      try {
        const fromCompact = periodFrom.replace(/-/g, '');
        const toCompact = periodTo.replace(/-/g, '');
        const ref = collection(firestore, 'aggregations', 'operation', 'days');
        const snap = await getDocs(
          query(
            ref,
            where(documentId(), '>=', fromCompact),
            where(documentId(), '<=', toCompact),
          ),
        );
        const docs: DailyOperation[] = [];
        for (const d of snap.docs) {
          const parsed = DailyOperationSchema.safeParse(d.data());
          if (parsed.success) docs.push(parsed.data);
          else console.warn(`${TAG}: ${d.id} ignorado`, parsed.error.issues);
        }
        if (guard.cancelled) return;
        setState({ kind: 'READY', summary: summarize(docs), days: docs.length });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: failed`, err);
        // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
        reportNonFatal('admin_use_operation_failed', {
          site: 'failed',
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, [periodFrom, periodTo]);

  return state;
}
